import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Fuel, Cog, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const VEHICLES = [
  {
    id: "panda", cat: "city", name: "Fiat Panda",
    img: "https://images.unsplash.com/photo-1613575049432-fe596a0a97a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw3fHxGaWF0JTIwUGFuZGElMjBtb2Rlcm58ZW58MHx8fHwxNzkyNDI4NjM1fDA&ixlib=rb-4.1.0&q=85",
    specs: ["5 posti", "Manuale", "Benzina"], price: "29",
  },
  {
    id: "fiat500", cat: "city", name: "Fiat 500",
    img: "https://images.unsplash.com/photo-1536667842290-7602f6a43a2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxGaWF0JTIwNTAwfGVufDB8fHx8MTc5MjQyODYyN3ww&ixlib=rb-4.1.0&q=85",
    specs: ["4 posti", "Manuale", "Benzina"], price: "32",
  },
  {
    id: "renegade", cat: "suv", name: "Jeep Renegade",
    img: "https://images.unsplash.com/photo-1602580931216-5fc46911d419?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxKZWVwJTIwUmVuZWdhZGV8ZW58MHx8fHwxNzkyNDI4NjI3fDA&ixlib=rb-4.1.0&q=85",
    specs: ["5 posti", "Manuale", "Benzina"], price: "45",
  },
  {
    id: "audia3", cat: "suv", name: "Audi A3 Sportback",
    img: "https://images.unsplash.com/photo-1717711081688-985a7a3e6a9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjY2NzV8MHwxfHNlYXJjaHwyfHxBdWRpJTIwQTN8ZW58MHx8fHwxNzkyNDI4NjI3fDA&ixlib=rb-4.1.0&q=85",
    specs: ["5 posti", "Automatico", "Benzina"], price: "49",
  },
  {
    id: "ducato", cat: "vans", name: "Fiat Ducato",
    img: "https://images.unsplash.com/photo-1692279952855-4a72ce767dd8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxGaWF0JTIwRHVjYXRvfGVufDB8fHx8MTc5MjQyODYyN3ww&ixlib=rb-4.1.0&q=85",
    specs: ["13 m³", "3 posti", "Diesel"], price: "69",
  },
  {
    id: "transit", cat: "vans", name: "Ford Transit",
    img: "https://images.unsplash.com/photo-1688380303978-d7841183b28c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxGb3JkJTIwVHJhbnNpdHxlbnwwfHx8MTc5MjQyODYyN3ww&ixlib=rb-4.1.0&q=85",
    specs: ["6 m³", "3 posti", "Diesel"], price: "59",
  },
  {
    id: "berlingo", cat: "vans", name: "Citroën Berlingo",
    img: "https://images.unsplash.com/photo-1768389533475-edc8b2bb9c7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6Njd8MHwxfHNlYXJjaHwxfHxDaXRyb2VuJTIwQmVybGluZ28lMjB2YW58ZW58MHx8fHwxNzkyNDI4NjM1fDA&ixlib=rb-4.1.0&q=85",
    specs: ["3,9 m³", "3 posti", "Diesel"], price: "45",
  },
];

const TABS = [
  { id: "all", label: "Tutti", testid: "fleet-tab-all" },
  { id: "city", label: "Auto Urbane", testid: "fleet-tab-city" },
  { id: "suv", label: "Berline & SUV", testid: "fleet-tab-suv" },
  { id: "vans", label: "Furgoni & Commerciali", testid: "fleet-tab-vans" },
];

const specIcon = (i) => (i === 0 ? <Users className="w-3.5 h-3.5" /> : i === 1 ? <Cog className="w-3.5 h-3.5" /> : <Fuel className="w-3.5 h-3.5" />);

export const Fleet = () => {
  const [tab, setTab] = useState("all");
  const visible = tab === "all" ? VEHICLES : VEHICLES.filter((v) => v.cat === tab);

  return (
    <section id="flotta" className="py-24 sm:py-32 bg-white" data-testid="fleet-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <Reveal>
          <p className="text-xs font-mono font-semibold uppercase tracking-[0.25em] text-emerald-700 mb-4">La flotta</p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 max-w-xl">
              Un veicolo per ogni esigenza, dal centro città al cantiere.
            </h2>
            <div className="flex flex-wrap gap-2" role="tablist">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={tab === t.id}
                  data-testid={t.testid}
                  onClick={() => setTab(t.id)}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                    tab === t.id
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-800"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((v) => (
              <motion.article
                layout
                key={v.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="group rounded-3xl border border-slate-200 bg-[#fafafa] overflow-hidden hover:border-emerald-600/40 hover:shadow-xl hover:shadow-emerald-900/5 transition-[border-color,box-shadow] duration-500"
                data-testid={`fleet-card-${v.id}`}
              >
                <div className="overflow-hidden h-52">
                  <img
                    src={v.img}
                    alt={v.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display text-lg font-bold text-slate-900">{v.name}</h3>
                    <p className="text-right leading-none shrink-0">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block">da</span>
                      <span className="font-display text-xl font-extrabold text-emerald-700">€{v.price}</span>
                      <span className="text-xs text-slate-500">/giorno</span>
                    </p>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {v.specs.map((s, i) => (
                      <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
                        {specIcon(i)}
                        {s}
                      </span>
                    ))}
                  </div>
                  <a
                    href="#preventivo"
                    data-testid={`fleet-card-book-${v.id}`}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 group-hover:text-emerald-700 transition-colors duration-300"
                  >
                    Richiedi questo veicolo
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </a>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
